import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X, HandHeart, PlayCircle, FacebookLogo, InstagramLogo, YoutubeLogo } from "@phosphor-icons/react";
import { CHURCH } from "@/lib/church";

const NAV = [
    { to: "/", label: "Home", key: "home" },
    { to: "/about", label: "About", key: "about" },
    { to: "/pastor", label: "Pastor", key: "pastor" },
    { to: "/house2house", label: <>House<span className="text-[#FFC72C] font-black text-[1.3em] align-middle">²</span>House</>, key: "house2house" },
    { to: "/sermons", label: "Sermons", key: "sermons" },
    { to: "/events", label: "Events", key: "events" },
    { to: "/ministries", label: "Ministries", key: "ministries" },
    { to: "/friends", label: "Friends", key: "friends" },
    { to: "/contact", label: "Contact", key: "contact" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <>
            {/* Top social/info bar */}
            <div className="hidden md:block bg-[#4C1D95] text-white text-xs">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-9">
                    <div className="flex items-center gap-5 text-white/80">
                        <a href={CHURCH.phoneHref} className="hover:text-[#FFC72C]" data-testid="top-phone">{CHURCH.phone}</a>
                        <span className="hidden lg:inline">·</span>
                        <span className="hidden lg:inline">{CHURCH.address}</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-white/60">Follow us</span>
                        <a href={CHURCH.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-[#FFC72C]"><FacebookLogo size={14} weight="bold" /></a>
                        <a href={CHURCH.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-[#FFC72C]"><InstagramLogo size={14} weight="bold" /></a>
                        <a href={CHURCH.social.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-[#FFC72C]"><YoutubeLogo size={14} weight="bold" /></a>
                    </div>
                </div>
            </div>

            <header
                data-testid="site-header"
                className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 ${
                    scrolled ? "bg-white shadow-md" : "bg-white"
                }`}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
                    <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
                        <span className="w-12 h-12 bg-[#4C1D95] flex items-center justify-center font-black text-[#FFC72C] text-xl tracking-tighter">
                            GM
                        </span>
                        <span className="hidden sm:block">
                            <span className="block text-base lg:text-lg leading-tight font-black tracking-tight text-[#2E1A47]">
                                GREATER MOUNT OLIVE
                            </span>
                            <span className="block text-[10px] uppercase tracking-[0.25em] text-[#4C1D95] font-bold">
                                A.M.E. Church · Philadelphia
                            </span>
                        </span>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-7">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={`nav-link-${item.key}`}
                                className={({ isActive }) =>
                                    `text-xs uppercase tracking-[0.15em] font-bold transition-colors ${
                                        isActive ? "text-[#4C1D95]" : "text-[#2E1A47] hover:text-[#4C1D95]"
                                    }`
                                }
                                end={item.to === "/"}
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <a
                            href={CHURCH.streamUrl}
                            data-testid="header-stream-btn"
                            className="hidden sm:inline-flex items-center gap-2 bg-[#4C1D95] hover:bg-[#4C1D95] text-white px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
                        >
                            <PlayCircle size={14} weight="fill" /> Stream
                        </a>
                        <a
                            href={CHURCH.giveUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="header-give-btn"
                            className="inline-flex items-center gap-2 bg-[#FFC72C] hover:bg-[#E6B327] text-[#2E1A47] px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
                        >
                            <HandHeart size={14} weight="bold" /> Give
                        </a>
                        <button
                            type="button"
                            onClick={() => setOpen((v) => !v)}
                            data-testid="mobile-menu-toggle"
                            className="lg:hidden p-2 text-[#2E1A47]"
                            aria-label="Toggle menu"
                        >
                            {open ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
                        </button>
                    </div>
                </div>

                {open && (
                    <div data-testid="mobile-menu" className="lg:hidden border-t border-slate-200 bg-white">
                        <nav className="flex flex-col py-2 px-4">
                            {NAV.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    data-testid={`mobile-nav-${item.key}`}
                                    className={({ isActive }) =>
                                        `py-3 px-2 text-sm uppercase tracking-[0.15em] font-bold border-b border-slate-100 ${
                                            isActive ? "text-[#4C1D95]" : "text-[#2E1A47]"
                                        }`
                                    }
                                    end={item.to === "/"}
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                        </nav>
                    </div>
                )}
            </header>
        </>
    );
}
